import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DashboardContentShell, KPIBar, DashboardSection, DashboardWidget } from '../shared/layout/DashboardLayout';
import { MetricCard } from '../shared/layout/CardLayout';
import { useAlertSubscription } from '../shared/hooks/useAlertSubscription';
import { useTelemetrySubscription } from '../shared/hooks/useTelemetrySubscription';
import { useRealTimeData } from '../shared/hooks/useRealTimeData';
import { Alert, Machine, TelemetrySample } from '@cbm/shared';

interface MachineHealthSummary {
  machineId: string;
  machineName: string;
  healthScore: number;
  alertCount: number;
  uptime: number;
  maintenanceStatus: 'current' | 'due' | 'overdue';
}

interface CorrelationPattern {
  pattern: string;
  affectedMachines: string[];
  confidence: number;
  components: string[];
  description: string;
}

type MachineStats = {
  machineId: string;
  samples: number;
  lastTs?: number;
};

function avg(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function lastN<T>(arr: T[], n: number): T[] {
  if (arr.length <= n) return arr;
  return arr.slice(arr.length - n);
}

function getTag(sample: TelemetrySample, name: string): number {
  return sample.tags.find(t => t.name === name)?.value ?? 0;
}

export function OEMServiceDashboard() {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [machineHealth, setMachineHealth] = useState<MachineHealthSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMachine, setSelectedMachine] = useState<string | null>(null);
  const [loadingHistorical, setLoadingHistorical] = useState(false);

  const [correlations, setCorrelations] = useState<CorrelationPattern[]>([]);
  const [telemetryHistory, setTelemetryHistory] = useState<Map<string, TelemetrySample[]>>(new Map());
  const [evidenceAlerts, setEvidenceAlerts] = useState<Alert[]>([]);
  const [downloadingEvidence, setDownloadingEvidence] = useState(false);

  const { alerts } = useAlertSubscription();
  const { samples, subscribe } = useTelemetrySubscription();
  const { isConnected } = useRealTimeData(null as any, () => true as any, () => true as any, () => true as any);

  const subscribedRef = useRef<Set<string>>(new Set());

  // Filter alerts with evidence (both OEM and PLANT for OEM Service dashboard)
  useEffect(() => {
    console.log('All alerts:', alerts.length, alerts);
    const alertsWithEvidence = alerts.filter(
      (alert: Alert) => alert.evidenceRefs && alert.evidenceRefs.length > 0
    );
    console.log('Alerts with evidence:', alertsWithEvidence.length, alertsWithEvidence);
    setEvidenceAlerts(alertsWithEvidence);
  }, [alerts]);

  const downloadAllEvidence = async () => {
    setDownloadingEvidence(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_BASE_URL;
      const response = await fetch(`${apiUrl}/alerts/evidence/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.downloadUrl) {
          window.location.href = data.downloadUrl;
        }
      }
    } catch (error) {
      console.error('Failed to download evidence:', error);
    } finally {
      setDownloadingEvidence(false);
    }
  };

  const downloadSingleEvidence = async (alertId: string) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_BASE_URL;
      const response = await fetch(`${apiUrl}/alerts/${alertId}/evidence`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        for (const url of data.evidenceUrls) {
          const link = document.createElement('a');
          link.href = url;
          link.download = `evidence-${alertId}.json`;
          link.click();
        }
      }
    } catch (error) {
      console.error('Failed to download evidence:', error);
    }
  };

  useEffect(() => {
    const mockMachines: Machine[] = [
      {
        pk: 'TENANT#demo',
        sk: 'MACHINE#M-001',
        displayName: 'Production Line A - Station 1',
        designVariant: 'Model X1',
        usageProfile: 'normal',
        createdAt: new Date().toISOString(),
      },
      {
        pk: 'TENANT#demo',
        sk: 'MACHINE#M-002',
        displayName: 'Production Line A - Station 2',
        designVariant: 'Model X1',
        usageProfile: 'aggressive',
        createdAt: new Date().toISOString(),
      },
      {
        pk: 'TENANT#demo',
        sk: 'MACHINE#M-003',
        displayName: 'Production Line B - Station 1',
        designVariant: 'Model X2',
        usageProfile: 'poor_pm',
        createdAt: new Date().toISOString(),
      },
    ];

    const mockHealth: MachineHealthSummary[] = [
      { machineId: 'M-001', machineName: 'Production Line A - Station 1', healthScore: 87, alertCount: 2, uptime: 94.2, maintenanceStatus: 'current' },
      { machineId: 'M-002', machineName: 'Production Line A - Station 2', healthScore: 72, alertCount: 5, uptime: 89.1, maintenanceStatus: 'due' },
      { machineId: 'M-003', machineName: 'Production Line B - Station 1', healthScore: 91, alertCount: 1, uptime: 97.8, maintenanceStatus: 'current' },
    ];

    setMachines(mockMachines);
    setMachineHealth(mockHealth);
    setLoading(false);

    // Fetch alerts with evidence on mount
    const fetchAlerts = async () => {
      try {
        const token = localStorage.getItem('token');
        const apiUrl = import.meta.env.VITE_API_BASE_URL;
        const allAlerts: Alert[] = [];
        
        for (const machine of mockMachines) {
          const machineId = machine.sk.split('#')[1];
          const response = await fetch(`${apiUrl}/machines/${machineId}/alerts`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const alerts = await response.json();
            allAlerts.push(...alerts);
          }
        }
        
        const withEvidence = allAlerts.filter(a => a.evidenceRefs && a.evidenceRefs.length > 0);
        console.log('Initial alerts with evidence:', withEvidence.length, withEvidence);
        setEvidenceAlerts(withEvidence);
      } catch (error) {
        console.error('Failed to fetch alerts:', error);
      }
    };
    
    fetchAlerts();
  }, []);

  // Subscribe once connected
  useEffect(() => {
    if (!isConnected) return;
    if (machines.length === 0) return;

    machines.forEach(machine => {
      const machineId = machine.sk.split('#')[1];
      if (subscribedRef.current.has(machineId)) return;
      subscribedRef.current.add(machineId);
      console.log('OEM Service: Subscribing to', machineId);
      subscribe(machineId);
    });
  }, [isConnected, machines, subscribe]);

  const activeMachineIds = useMemo(() => {
    return [...new Set(samples.map(s => s.machineId))];
  }, [samples]);

  const machineStats = useMemo((): MachineStats[] => {
    const map = new Map<string, MachineStats>();
    samples.forEach(s => {
      const existing = map.get(s.machineId) ?? { machineId: s.machineId, samples: 0 };
      existing.samples += 1;
      existing.lastTs = Math.max(existing.lastTs ?? 0, new Date(s.timestamp).getTime());
      map.set(s.machineId, existing);
    });
    return Array.from(map.values()).sort((a, b) => (b.samples - a.samples));
  }, [samples]);

  const loadHistoricalData = async () => {
    setLoadingHistorical(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_BASE_URL;
      const response = await fetch(`${apiUrl}/telemetry/historical?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const samples: TelemetrySample[] = data.samples || [];
        setTelemetryHistory(prev => {
          const updated = new Map(prev);
          samples.forEach(sample => {
            const history = updated.get(sample.machineId) || [];
            history.push(sample);
            updated.set(sample.machineId, history.slice(-100));
          });
          return updated;
        });
      }
    } catch (error) {
      console.error('Failed to load historical data:', error);
    } finally {
      setLoadingHistorical(false);
    }
  };

  // Real-time correlation analysis
  useEffect(() => {
    if (samples.length === 0) return;

    setTelemetryHistory(prev => {
      const updated = new Map(prev);
      samples.forEach(sample => {
        const history = updated.get(sample.machineId) || [];
        history.push(sample);
        updated.set(sample.machineId, history.slice(-80));
      });

      const machineIds = Array.from(updated.keys());
      const patterns: CorrelationPattern[] = [];

      console.log('OEM Service: Analyzing', machineIds.length, 'machines with history');

      const recentAvg = (id: string, tag: string) => {
        const history = updated.get(id) || [];
        const recent = lastN(history, 8);
        return avg(recent.map(s => getTag(s, tag)));
      };

      // Cross-machine correlations
      if (machineIds.length >= 2) {
        const hot = machineIds.filter(id => recentAvg(id, 'temperature') > 70);
        if (hot.length >= 2) {
          patterns.push({
            pattern: 'Simultaneous Temperature Elevation',
            affectedMachines: hot,
            confidence: 82 + Math.random() * 12,
            components: ['temperature', 'cooling_system'],
            description: 'Multiple machines show elevated temperatures simultaneously. Investigate ambient conditions or shared cooling infrastructure.',
          });
        }

        const lowPressure = machineIds.filter(id => recentAvg(id, 'pressure') > 0 && recentAvg(id, 'pressure') < 50);
        if (lowPressure.length >= 2) {
          patterns.push({
            pattern: 'System-Wide Pressure Drop',
            affectedMachines: lowPressure,
            confidence: 80 + Math.random() * 10,
            components: ['pressure', 'pneumatic_system', 'compressor'],
            description: 'Multiple machines show low pressure. Check compressor output, supply lines, and filters.',
          });
        }
      }

      // Single-machine insights
      machineIds.forEach(id => {
        const history = updated.get(id) || [];
        if (history.length < 6) return;

        const recent = lastN(history, 10);
        const older = history.slice(0, Math.min(10, history.length - 1));

        const tRecent = avg(recent.map(s => getTag(s, 'temperature')));
        const tOlder = avg(older.map(s => getTag(s, 'temperature')));
        const vRecent = avg(recent.map(s => getTag(s, 'vibration_level')));
        const vOlder = avg(older.map(s => getTag(s, 'vibration_level')));
        const cRecent = avg(recent.map(s => getTag(s, 'cycle_time')));
        const cOlder = avg(older.map(s => getTag(s, 'cycle_time')));

        const machineAlerts = alerts.filter((a: Alert) => a.machineId === id && a.severity !== 'EARLY');

        if (tRecent > Math.max(45, tOlder + 3)) {
          patterns.push({
            pattern: 'Thermal Drift Detected',
            affectedMachines: [id],
            confidence: 70 + Math.random() * 18,
            components: ['temperature', 'cooling_system'],
            description: 'Temperature is trending upward versus earlier samples. Validate coolant flow, fan operation, and ambient conditions.',
          });
        }

        if (vRecent > Math.max(1.5, vOlder + 0.25)) {
          patterns.push({
            pattern: 'Vibration Increase Detected',
            affectedMachines: [id],
            confidence: 72 + Math.random() * 16,
            components: ['vibration', 'bearing', 'alignment'],
            description: 'Vibration levels are trending higher. Inspect bearings, alignment, and mounting rigidity.',
          });
        }

        if (cRecent > Math.max(20, cOlder + 2)) {
          patterns.push({
            pattern: 'Cycle Time Degradation',
            affectedMachines: [id],
            confidence: 68 + Math.random() * 18,
            components: ['cycle_time', 'performance'],
            description: 'Cycle time is increasing compared to earlier samples. Investigate bottlenecks, tool wear, or feed rate constraints.',
          });
        }

        if (vRecent > 2.0 && machineAlerts.length > 0) {
          patterns.push({
            pattern: 'Vibration-Alert Correlation',
            affectedMachines: [id],
            confidence: 78 + Math.random() * 14,
            components: ['vibration', 'alerts'],
            description: 'Elevated vibration coincides with active alerts. Mechanical wear or misalignment is likely.',
          });
        }
      });

      const dedup = new Map<string, CorrelationPattern>();
      patterns.forEach(p => {
        const key = `${p.pattern}::${p.affectedMachines.slice().sort().join(',')}`;
        if (!dedup.has(key)) dedup.set(key, p);
      });

      const finalPatterns = Array.from(dedup.values())
        .sort((a, b) => b.confidence - a.confidence)
        .slice(0, 8);

      setCorrelations(finalPatterns);
      console.log('OEM Service: Detected correlations:', finalPatterns.length);

      return updated;
    });
  }, [samples, alerts]);

  const oemAlerts = alerts.filter(alert => alert.owner === 'OEM');
  const avgHealth = machineHealth.reduce((sum, m) => sum + m.healthScore, 0) / (machineHealth.length || 1);
  const totalAlerts = oemAlerts.length;
  const avgUptime = machineHealth.reduce((sum, m) => sum + m.uptime, 0) / (machineHealth.length || 1);

  if (loading) {
    return (
      <DashboardContentShell>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </DashboardContentShell>
    );
  }

  return (
    <DashboardContentShell>
      <KPIBar
        metrics={[
          { label: 'Avg Health', value: avgHealth.toFixed(1), unit: '%', status: avgHealth >= 80 ? 'good' : avgHealth >= 60 ? 'warning' : 'critical' },
          { label: 'OEM Alerts', value: totalAlerts, status: totalAlerts > 10 ? 'critical' : totalAlerts > 5 ? 'warning' : 'good' },
          { label: 'Avg Uptime', value: avgUptime.toFixed(1), unit: '%', status: avgUptime >= 95 ? 'good' : avgUptime >= 90 ? 'warning' : 'critical' },
          { label: 'Streaming', value: activeMachineIds.length, status: activeMachineIds.length >= 2 ? 'good' : 'warning' },
          { label: 'Status', value: isConnected ? 'LIVE' : 'OFFLINE', status: isConnected ? 'good' : 'critical' },
        ]}
      />

      <div className="mb-4">
        <label className="block text-xs font-medium text-slate-400 uppercase mb-2">Machine Filter</label>
        <select
          value={selectedMachine || ''}
          onChange={(e) => setSelectedMachine(e.target.value || null)}
          className="px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white focus:outline-none focus:border-blue-500"
        >
          <option value="">All Machines</option>
          {machines.map(machine => (
            <option key={machine.sk} value={machine.sk.split('#')[1]}>
              {machine.displayName}
            </option>
          ))}
        </select>
      </div>

      <DashboardSection title="Machine Health Summary" subtitle="Equipment condition monitoring">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {machineHealth
            .filter(health => !selectedMachine || health.machineId === selectedMachine)
            .map(health => (
              <div key={health.machineId} className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">{health.machineName}</h3>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    health.healthScore >= 85 ? 'bg-emerald-500/20 text-emerald-400' :
                    health.healthScore >= 70 ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {health.healthScore}%
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <MetricCard title="Alerts" value={health.alertCount} />
                  <MetricCard title="Uptime" value={`${health.uptime.toFixed(1)}%`} />
                </div>

                <div className="mt-3 flex justify-between items-center p-2 bg-slate-900 border border-slate-600 rounded">
                  <span className="text-sm text-slate-300">Maintenance</span>
                  <span className={`text-xs font-medium px-2 py-1 rounded ${
                    health.maintenanceStatus === 'current' ? 'bg-emerald-500/20 text-emerald-400' :
                    health.maintenanceStatus === 'due' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {health.maintenanceStatus.toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
        </div>
      </DashboardSection>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DashboardWidget title="Correlation Analysis">
          <div className="mb-3 flex justify-end">
            <button
              onClick={loadHistoricalData}
              disabled={loadingHistorical}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white rounded text-sm transition-colors flex items-center gap-2"
            >
              {loadingHistorical ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Loading...
                </>
              ) : (
                <>📥 Load Historical</>
              )}
            </button>
          </div>
          {correlations.length === 0 ? (
            <div className="text-center text-slate-400 py-8">
              <div className="mb-4">
                <div className="w-16 h-16 mx-auto bg-slate-700 rounded-lg flex items-center justify-center mb-3">
                  <span className="text-2xl">📊</span>
                </div>
                <p className="text-sm">Analyzing cross-machine patterns...</p>
                <p className="text-xs text-slate-500 mt-1">
                  {activeMachineIds.length < 2
                    ? `Only ${activeMachineIds.length} machine streaming. Start 2+ simulations for cross-machine correlations.`
                    : 'Collecting telemetry window to compute correlations.'}
                </p>
              </div>

              {machineStats.length > 0 && (
                <div className="max-w-md mx-auto text-left">
                  <div className="text-xs text-slate-500 mb-2">Live telemetry streams</div>
                  <div className="space-y-2">
                    {machineStats.slice(0, 4).map(s => (
                      <div key={s.machineId} className="flex items-center justify-between bg-slate-900 border border-slate-700 rounded px-3 py-2">
                        <span className="text-xs text-slate-300">{s.machineId}</span>
                        <span className="text-xs text-slate-400">{s.samples} samples</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {correlations.map((corr, idx) => (
                <div key={idx} className="p-3 bg-slate-900 border border-slate-600 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-white">{corr.pattern}</div>
                      <div className="text-xs text-slate-400 mt-1">{corr.description}</div>
                    </div>
                    <div className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                      corr.confidence >= 85 ? 'bg-emerald-500/20 text-emerald-400' :
                      corr.confidence >= 75 ? 'bg-amber-500/20 text-amber-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {corr.confidence.toFixed(0)}%
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-300 mt-2">
                    <span className="text-slate-400">Machines:</span>
                    <span>{corr.affectedMachines.join(', ')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-300 mt-1">
                    <span className="text-slate-400">Components:</span>
                    <span>{corr.components.join(', ')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DashboardWidget>

        <DashboardWidget title="Evidence Downloads">
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-slate-900 border border-slate-600 rounded">
              <span className="text-sm text-slate-300">{evidenceAlerts.length} alerts with evidence</span>
              <button
                onClick={downloadAllEvidence}
                disabled={downloadingEvidence || evidenceAlerts.length === 0}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white rounded text-sm transition-colors"
              >
                {downloadingEvidence ? 'Downloading...' : 'Download All'}
              </button>
            </div>
            {evidenceAlerts.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {evidenceAlerts.map((alert) => (
                  <div key={alert.alertId} className="flex justify-between items-center p-3 bg-slate-900 border border-slate-600 rounded">
                    <div className="flex-1">
                      <div className="text-sm text-white">{alert.summary}</div>
                      <div className="text-xs text-slate-400 mt-1">{alert.machineId} • {alert.severity}</div>
                    </div>
                    <button
                      onClick={() => downloadSingleEvidence(alert.alertId)}
                      className="ml-3 px-2 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs transition-colors"
                    >
                      📥 Download
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-slate-400 py-4">
                <div className="w-12 h-12 mx-auto bg-slate-700 rounded-lg flex items-center justify-center mb-2">
                  <span className="text-xl">📁</span>
                </div>
                <p className="text-xs">No evidence files available</p>
              </div>
            )}
          </div>
        </DashboardWidget>
      </div>
    </DashboardContentShell>
  );
}
